import React from 'react';
import { useCompany } from '@/context/CompanyContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Users, Crown, Trash2, UserMinus } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

export const TeamMembersList: React.FC = () => {
  const { members, userRole, removeMember } = useCompany();

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <Badge variant="default" className="gap-1"><Crown className="w-3 h-3" />Admin</Badge>;
      case 'vendeur':
        return <Badge variant="secondary">Vendeur</Badge>;
      default:
        return <Badge variant="outline">Membre</Badge>;
    }
  };

  const getInitials = (name: string | null | undefined, email: string) => {
    if (name && name.trim()) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U'; // Default to 'U' for User when no name or email available
  };

  const handleRemoveMember = async (memberId: string) => {
    await removeMember(memberId);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/10 to-green-600/20 transition-transform duration-200 hover:scale-105">
            <Users className="w-6 h-6 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium mb-1 text-green-700 dark:text-green-300">
              Équipe
            </p>
            <p className="text-xl font-bold text-green-800 dark:text-green-200">
              Membres de l'équipe ({members.length})
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-3 rounded-lg border bg-card"
            >
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-primary/10 text-primary font-medium">
                    {getInitials(member.profile?.full_name, '')}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <div className="font-medium">
                    {member.profile?.full_name || 'Utilisateur'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Membre de l'équipe
                  </div>
                </div>
                
                {getRoleBadge(member.role)}
              </div>

              {userRole === 'admin' && member.role !== 'admin' && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                      <UserMinus className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Retirer ce membre ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Êtes-vous sûr de vouloir retirer <strong>{member.profile?.full_name || 'ce membre'}</strong> de l'équipe ? 
                        Cette action ne peut pas être annulée.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleRemoveMember(member.id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Retirer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          ))}
          
          {members.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun membre dans l'équipe</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};